import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography, List, ListItem, TextField, Grid, CardContent, Card, CircularProgress, Alert
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import NietBeschikbaarDialog from './NietBeschikbaarDialog';
import axios from 'axios';

const ReserveringPopup = ({     
    handleMaterialClickForOverlap,
    overlappingReservations,
    openReservationDialog,
    setOpenReservationDialog,
    selectedMaterials,
    setSelectedMaterials,
    postOrUpdateReservering,
    open, 
    onClose, 
    currentReservation, 
    getDatesInRange, 
    reservedMaterials, 
    reservations 
}) => {
  const [startDate, setStartDate] = useState(currentReservation?.fields?.Start ? new Date(currentReservation.fields.Start) : new Date());
  const [endDate, setEndDate] = useState(currentReservation?.fields?.Eind ? new Date(currentReservation.fields.Eind) : new Date());
  const [unavailableMaterials, setUnavailableMaterials] = useState([]);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  
    // Sla de oorspronkelijke data op als referentie
    const [originalStartDate, setOriginalStartDate] = useState(currentReservation?.fields?.Start ? new Date(currentReservation.fields.Start) : new Date());
    const [originalEndDate, setOriginalEndDate] = useState(currentReservation?.fields?.Eind ? new Date(currentReservation.fields.Eind) : new Date());

    useEffect(() => {
        if (open && currentReservation?.fields?.Start && currentReservation?.fields?.Eind) {
            setStartDate(new Date(currentReservation.fields.Start));
            setEndDate(new Date(currentReservation.fields.Eind));
            setOriginalStartDate(new Date(currentReservation.fields.Start));
            setOriginalEndDate(new Date(currentReservation.fields.Eind));
            setUnavailableMaterials([]);
      setAvailableMaterials([]);
      setError('');
        }
    }, [open, currentReservation?.fields?.Start, currentReservation?.fields?.Eind]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setError(''); // Clear error when user changes date
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setError(''); // Clear error when user changes date
  };

  // Backend check voor beschikbaarheid
  const checkBackendAvailability = async () => {
    // Fallback: als reservedMaterials leeg is, gebruik selectedMaterials
    const materialsToCheck = Array.isArray(reservedMaterials) && reservedMaterials.length > 0
      ? reservedMaterials
      : (Array.isArray(selectedMaterials) && selectedMaterials.length > 0 ? selectedMaterials : []);
    const materialIds = materialsToCheck.map(m => m.id);
  
  
    if (!currentReservation || !currentReservation.id) {
      setError('Geen geldige reservering geselecteerd.');
      return { unavailable: [] };
    }
    if (!startDate || !endDate || startDate > endDate) {
      setError('Selecteer geldige datums');
      return { unavailable: [] };
    }
    if (!materialIds || materialIds.length === 0) {
      setError('Geen materialen geselecteerd voor deze reservering.');
      return { unavailable: [] };
    }
  
    try {
      const response = await axios.post(`${process.env.REACT_APP_CURRENT_URL}/Api/check-availability`, {
        materialIds,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        currentReservationId: currentReservation.id
      });
      return response.data;
    } catch (error) {
      console.error('Error checking backend availability:', error);
      setError('Fout bij controleren beschikbaarheid');
      return { unavailable: [] };
    }
  };

  const handleCheckAvailability = async () => {
    setIsCheckingAvailability(true);
    setError('');
    
    try {
      // Eerst backend check
      const backendResult = await checkBackendAvailability();
      
      
      // Als backend geen resultaten geeft, gebruik frontend check als fallback
      if (!backendResult.unavailable || backendResult.unavailable.length === 0) {
        // Frontend check gebaseerd op de werkende logica uit SimpleMaterialWizard
        const frontendUnavailable = [];
        const frontendAvailable = [];
        
        for (const material of reservedMaterials) {
          const isAvailable = isMaterialAvailableForDateRange(material, startDate, endDate);
          if (!isAvailable) {
            frontendUnavailable.push(material);
          } else {
            frontendAvailable.push(material);
          }
        }
        
        setUnavailableMaterials(frontendUnavailable);
        setAvailableMaterials(frontendAvailable);
      } else {
        // Backend stuurt nu volledige materiaal-objecten terug
        const backendUnavailableIds = new Set(backendResult.unavailable.map(material => material.id));
        const finalUnavailable = backendResult.unavailable; // Gebruik direct de backend resultaten
        const finalAvailable = reservedMaterials.filter(material => 
          !backendUnavailableIds.has(material.id)
        );
        
        setUnavailableMaterials(finalUnavailable);
        setAvailableMaterials(finalAvailable);
      }
      
    } catch (error) {
      console.error('Error checking availability:', error);
      setError('Fout bij controleren beschikbaarheid');
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  // Frontend beschikbaarheidscheck gebaseerd op de werkende logica uit SimpleMaterialWizard
  const isMaterialAvailableForDateRange = (material, newStartDate, newEndDate) => {
    const materialReservations = reservations[material.id] || [];
    const startDate = new Date(newStartDate);
    const endDate = new Date(newEndDate);
    
    // Check for overlapping reservations (excluding current reservation)
    const hasConflict = materialReservations.some(reservation => {
      // Skip if it's the current reservation
      if (reservation.reservationId === currentReservation?.id) {
        return false;
      }
      
      const resStart = new Date(reservation.startDate);
      const resEnd = new Date(reservation.endDate);
      
      return (resStart <= endDate && resEnd >= startDate);
    });
    
    return !hasConflict;
  };

  const handleSaveReservation = async () => {
    setIsSaving(true);
    setError('');
    
    try {
      // Eerst backend check voor definitieve beschikbaarheid
      const backendResult = await checkBackendAvailability();
      
      if (backendResult.unavailable.length > 0) {
        // Er zijn conflicten, toon bevestigingsdialoog
        const backendUnavailableIds = new Set(backendResult.unavailable.map(material => material.id));
        const conflictingMaterials = backendResult.unavailable; // Gebruik direct de backend resultaten
        setUnavailableMaterials(conflictingMaterials);
        setAvailableMaterials(reservedMaterials.filter(material => 
          !backendUnavailableIds.has(material.id)
        ));
        setConfirmDialogOpen(true);
        return;
      }
      
      // Alles beschikbaar, direct opslaan
      await saveReservation();
      
    } catch (error) {
      console.error('Error saving reservation:', error);
      if (error.response?.status === 409) {
        setError('Sommige materialen zijn net gereserveerd door een andere gebruiker. Probeer opnieuw.');
      } else {
        setError('Fout bij opslaan reservering');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const saveReservation = async () => {
    setConfirmDialogOpen(false);
    onClose();
    
    // Geef de volledige materiaal-objecten en datums door aan postOrUpdateReservering
    if (availableMaterials && availableMaterials.length > 0) {
      setSelectedMaterials(availableMaterials);
      await postOrUpdateReservering(availableMaterials, startDate, endDate);
    } else {
      setSelectedMaterials([]);
      await postOrUpdateReservering([], startDate, endDate);
    }
  };

  // Frontend check voor UI feedback (blijft bestaan voor directe feedback)
    const checkMaterialsAvailability = async (reservationId, newStartDatum, newEindDatum) => {
        const unavailableMaterials = [];
        const availableMaterials = [];
    
        const startDatum = new Date(newStartDatum);
        const eindDatum = new Date(newEindDatum);
            
        const selectedRange = getDatesInRange(startDatum, eindDatum);
        const selectedRangeOriginal = getDatesInRange(originalStartDate, originalEndDate);
    
        for (const material of reservedMaterials) {
            const isAvailable = await isMaterialAvailable(material, selectedRange, selectedRangeOriginal, reservationId);
            if (!isAvailable) {
                unavailableMaterials.push(material);
            } else {
                availableMaterials.push(material);
            }
        }
        
    return { unavailableMaterials, availableMaterials };
    };

    const isMaterialAvailable = (material, selectedRange, selectedRangeOriginal, reservationId) => {
        const reservedDatesForMaterial = reservations[material.id];
        if (!reservedDatesForMaterial) return true;
        
        const originalDatesSet = new Set(selectedRangeOriginal);
        const filteredReservedDates = reservedDatesForMaterial.filter(date => 
      !originalDatesSet.has(date)
        );

        for (const date of selectedRange) {
            if (filteredReservedDates.includes(date)) {
        return false;
      }
    }
    
    return true;
    };

    useEffect(() => {
    if (startDate && endDate && startDate <= endDate) {
      handleCheckAvailability();
    }
  }, [startDate, endDate]);

  return (
    <div>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          Datum wijzigen - Reservering {currentReservation?.fields?.reserveringNummer || 'Onbekend'}
        </DialogTitle>
      <DialogContent>
            <Grid container spacing={2}>
                {/* Informatie sectie */}
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Reservering: {currentReservation?.fields?.reserveringNummer || 'Onbekend'}</Typography>
                            <Typography variant="body2">DraaidagInfo: {currentReservation?.fields?.DraaidagnaamProjectNaam || 'Onbekend'}</Typography>
                            <Typography variant="body2">Camera Operator: {currentReservation?.fields?.CameraMan || 'Onbekend'}</Typography>
                            <Typography variant="body2">Aangemaakt door: {currentReservation?.fields?.SubmitterName || 'Onbekend'} ({currentReservation?.fields?.CreatedTime || 'Onbekend'})</Typography>
                  <Typography variant="body2">Laatst gewijzigd door: {currentReservation?.fields?.ModifiedByName || 'Onbekend'} ({currentReservation?.fields?.LastModifiedTime || 'Onbekend'})</Typography>
                        </CardContent>
                    </Card>
                </Grid>

            {/* Error melding */}
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}

                {/* Datumkiezers */}
                <Grid item xs={12} sm={6}>
                    <DatePicker 
                        selected={startDate} 
                        onChange={handleStartDateChange} 
                        dateFormat="dd/MM/yyyy" 
                        customInput={<TextField fullWidth label="Startdatum" />}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DatePicker 
                        selected={endDate} 
                        onChange={handleEndDateChange} 
                        dateFormat="dd/MM/yyyy" 
                        customInput={<TextField fullWidth label="Einddatum" />}
                    />
                </Grid>

            {/* Loading indicator */}
            {isCheckingAvailability && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent style={{ textAlign: 'center' }}>
                    <CircularProgress size={20} style={{ marginRight: 10 }} />
                    <Typography variant="body2">Controleren beschikbaarheid...</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

                {/* Niet beschikbare materialen */}
            {unavailableMaterials.length > 0 && !isCheckingAvailability && (
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                    <Typography variant="body2" color="error" gutterBottom>
                      ⚠️ Onderstaande materialen zijn niet beschikbaar voor de nieuwe data:
                                </Typography>
                    <List dense>
                                    {unavailableMaterials.map((material, index) => (
                                        <ListItem 
                                            key={index}
                          button
                          onClick={() => handleMaterialClickForOverlap(material)}
                                        >
                          <Typography variant="body2">
                            {material.fields.ID} - {material.fields.Omschrijving?.slice(0, 50)}...
                          </Typography>
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Beschikbare materialen */}
            {availableMaterials.length > 0 && !isCheckingAvailability && (
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent>
                    <Typography variant="body2" color="success.main" gutterBottom>
                      ✅ Beschikbaar materiaal voor nieuwe data:
                            </Typography>
                    <List dense>
                                {availableMaterials.map((material, index) => (
                                    <ListItem 
                                        key={index}
                          button
                          onClick={() => handleMaterialClickForOverlap(material)}
                                    >
                          <Typography variant="body2">
                            {material.fields.ID} - {material.fields.Omschrijving?.slice(0, 50)}...
                          </Typography>
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            )}
            </Grid>
        </DialogContent>
      <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            Annuleren
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSaveReservation}
            disabled={isSaving || isCheckingAvailability || startDate > endDate}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {isSaving ? 'Opslaan...' : 'Datum wijzigen'}
        </Button>
      </DialogActions>
    </Dialog>

    <NietBeschikbaarDialog
        open={openReservationDialog}
        onClose={() => setOpenReservationDialog(false)}
        reservations={overlappingReservations}
    />

    <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Bevestig Wijziging</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
                De volgende materialen zijn niet beschikbaar voor de nieuwe data en worden verwijderd uit de reservering:
            </Typography>
            <List>
                {unavailableMaterials.map((material, index) => (
                    <ListItem key={index}>
                <Typography variant="body2">
                  {material.fields.ID} - {material.fields.Omschrijving?.slice(0, 30)}...
                </Typography>
                    </ListItem>
                ))}
            </List>
          <Typography variant="body2" color="textSecondary">
                Wilt u doorgaan en de reservering bijwerken met alleen de beschikbare materialen?
            </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Annuleren
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={saveReservation}
            disabled={isSaving}
          >
            Bevestig wijziging
            </Button>
        </DialogActions>
    </Dialog>
    </div>
  );
};

export default ReserveringPopup;
